import { useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Plus, Pencil, Ban, RotateCcw, Store, AlertTriangle } from "lucide-react";
import BranchOperationModal from "./BranchOperationModal";

const branchCodeOf = (b) => (b.code || (b.pos_branch_code || "").split("-")[0] || "").toUpperCase();

export default function SupplierBranchOpsTab({ supplier, branches, onUpdated }) {
  const { t } = useTranslation();
  const ops = supplier.branch_operations || [];
  const [enabled, setEnabled] = useState(ops.length > 0);
  const [modalOp, setModalOp] = useState(undefined); // undefined = closed, null = new, {..} = editing
  const [busy, setBusy] = useState(false);

  const branchById = Object.fromEntries(branches.map((b) => [b.id, b]));

  const toggleActive = async (op) => {
    setBusy(true);
    try {
      const action = op.active === false ? "reactivate" : "deactivate";
      await api.post(`/suppliers/${supplier.id}/branch-operations/${op.id}/${action}`);
      toast.success(t("supplierEdit.branchOps.saved"));
      onUpdated && onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="supplier-branch-ops-tab">
      <label className="flex items-center justify-between gap-3 cursor-pointer">
        <div>
          <div className="text-sm font-semibold text-[#1A1A1A]">{t("supplierEdit.branchOps.toggleLabel")}</div>
          <div className="text-xs text-slate-500">{t("supplierEdit.branchOps.toggleHint")}</div>
        </div>
        <button
          type="button"
          data-testid="branch-ops-toggle"
          onClick={() => setEnabled((v) => !v)}
          className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${enabled ? "bg-emerald-500" : "bg-slate-300"}`}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${enabled ? "translate-x-5" : ""}`} />
        </button>
      </label>

      {enabled && (
        <div className="space-y-3">
          {ops.length === 0 && (
            <div className="text-center py-6 text-sm text-slate-500 bg-slate-50 rounded-xl border border-dashed border-slate-200">
              {t("supplierEdit.branchOps.empty")}
            </div>
          )}
          {ops.map((op) => {
            const br = branchById[op.branch_id];
            const inactive = op.active === false;
            return (
              <div key={op.id} data-testid={`branch-op-card-${branchCodeOf(br || {})}`}
                className={`p-3.5 rounded-xl border flex items-start justify-between gap-3 ${inactive ? "border-slate-200 bg-slate-50 opacity-60" : "border-slate-200 bg-white"}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#1A1A1A] text-white">
                      <Store className="w-3 h-3" /> {branchCodeOf(br || {})}
                    </span>
                    {op.display_label && <span className="text-xs text-slate-500">{op.display_label}</span>}
                    {inactive && <span className="text-[10px] font-semibold text-slate-500">{t("supplierEdit.branchOps.inactiveBadge")}</span>}
                  </div>
                  <div className="text-sm font-semibold text-[#1A1A1A] truncate">{op.vendor_name || t("supplierEdit.branchOps.noVendor")}</div>
                  <div className="text-xs text-slate-500 truncate">{op.orders_email || op.vendor_email || "—"}</div>
                  {op.bank_name && <div className="text-xs text-slate-400 truncate mt-0.5">{op.bank_name} · {op.bank_account_number}</div>}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button data-testid={`branch-op-edit-${branchCodeOf(br || {})}`} onClick={() => setModalOp(op)}
                    className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button data-testid={`branch-op-toggle-active-${branchCodeOf(br || {})}`} onClick={() => toggleActive(op)} disabled={busy}
                    className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500">
                    {inactive ? <RotateCcw className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            );
          })}

          {supplier.branches_authorized?.filter((bid) => !ops.some((o) => o.branch_id === bid && o.active !== false)).length > 0 && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{t("supplierEdit.branchOps.missingOpsWarning")}</span>
            </div>
          )}

          <button data-testid="branch-op-add-btn" onClick={() => setModalOp(null)}
            className="w-full py-2.5 border-2 border-dashed border-slate-300 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50 flex items-center justify-center gap-1.5">
            <Plus className="w-4 h-4" /> {t("supplierEdit.branchOps.addButton")}
          </button>
        </div>
      )}

      {modalOp !== undefined && (
        <BranchOperationModal
          supplier={supplier}
          branches={branches}
          editingOp={modalOp}
          onClose={() => setModalOp(undefined)}
          onSaved={onUpdated}
        />
      )}
    </div>
  );
}
